import { GreaterAreaDropdown, StateDropdown } from '@/components/dropdowns';
import { NeighborhoodImageCell } from '@/components/neighborhoods';
import type { CrudConfig } from '@startupquickstart/frontend';
import { Link } from 'react-router-dom';

// Define type based on our Prisma schema
export interface Neighborhood {
  id: string;
  name: string;
  status: 'published' | 'draft' | 'needs_review';
  description?: string;
  featuredImageId?: string;
  greaterAreaId: string;
  city: string;
  state: string;
  country: string;
  createdAt: string;
  updatedAt: string;
  // Relations for display
  greaterArea?: {
    id: string;
    name: string;
  };
  featuredImage?: {
    id: string;
    name: string;
    fileName: string;
    fileType: string;
    url?: string;
  };
  // Virtual field for large image display on detail pages
  featuredImageLarge?: {
    id: string;
    name: string;
    fileName: string;
    fileType: string;
    url?: string;
  };
}

/**
 * Neighborhood CRUD configuration
 */
export const neighborhoodCrudConfig: CrudConfig<Neighborhood> = {
  entityName: 'Neighborhood',
  entityNamePlural: 'Neighborhoods',

  endpoints: {
    list: '/neighborhoods',
    single: (id: string) => `/neighborhoods/${id}`,
    create: '/neighborhoods',
    update: (id: string) => `/neighborhoods/${id}`,
    delete: (id: string) => `/neighborhoods/${id}`,
  },

  fields: [
    {
      key: 'name',
      label: 'Name',
      type: 'text',
      required: true,
      placeholder: 'Enter neighborhood name',
      validation: {
        min: 2,
        max: 200,
      },
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      required: true,
      defaultValue: 'published',
      options: [
        { label: 'Draft', value: 'draft' },
        { label: 'Published', value: 'published' },
        { label: 'Needs Review', value: 'needs_review' },
      ],
    },
    {
      key: 'greaterAreaId',
      label: 'Greater Area',
      type: 'custom',
      required: true,
      placeholder: 'Select Greater Area',
      inputFormat: (
        value: string,
        onChange: (value: string) => void,
        field: { placeholder?: string },
      ) => (
        <GreaterAreaDropdown value={value} onChange={onChange} placeholder={field.placeholder} />
      ),
    },
    {
      key: 'greaterArea',
      label: 'Greater Area',
      type: 'text',
      required: false,
      placeholder: 'Greater Area',
      disableRowClick: true,
      format: (value: { id: string; name: string } | undefined) => {
        return (
          <Link className="text-primary" to={`/greater-areas/${value?.id}`}>
            {value?.name || 'N/A'}
          </Link>
        );
      },
    },
    {
      key: 'description',
      label: 'Description',
      type: 'richtext',
      required: false,
      placeholder: 'Enter description',
      validation: {
        max: 2000,
      },
    },
    {
      key: 'featuredImageId',
      label: 'Featured Image',
      type: 'image',
      required: false,
      placeholder: 'Featured Image ID',
    },
    {
      key: 'featuredImage',
      label: 'Image',
      type: 'custom',
      required: false,
      format: (_value: Neighborhood['featuredImage'], fullRecord?: Neighborhood) => {
        return <NeighborhoodImageCell neighborhood={fullRecord} />;
      },
    },
    {
      key: 'featuredImageLarge',
      label: 'Image',
      type: 'custom',
      required: false,
      format: (_value: Neighborhood['featuredImage'], fullRecord?: Neighborhood) => {
        return <NeighborhoodImageCell neighborhood={fullRecord} size="lg" />;
      },
    },
    {
      key: 'country',
      label: 'Country',
      type: 'text',
      required: true,
      defaultValue: 'US',
      placeholder: 'Country',
    },
    {
      key: 'state',
      label: 'State',
      type: 'custom',
      required: true,
      placeholder: 'Select State',
      inputFormat: (
        value: string,
        onChange: (value: string) => void,
        field: { placeholder?: string },
      ) => (
        <StateDropdown value={value} onChange={onChange} placeholder={field.placeholder} />
      ),
    },
    {
      key: 'city',
      label: 'City',
      type: 'text',
      required: true,
      placeholder: 'City',
    },
    {
      key: 'createdAt',
      label: 'Created At',
      type: 'datetime',
      readonly: true,
    },
    {
      key: 'updatedAt',
      label: 'Updated At',
      type: 'datetime',
      readonly: true,
    },
  ],

  listView: {
    fields: ['featuredImage', 'name', 'greaterArea', 'createdAt', 'updatedAt'],
    searchableFields: ['name', 'city', 'state'],
    sortableFields: ['name', 'status', 'city', 'state', 'createdAt', 'updatedAt'],
    defaultSort: {
      field: 'name',
      direction: 'asc',
    },
    itemsPerPage: 10,
    filters: [
      {
        key: 'status',
        label: 'Status',
        type: 'select',
        options: [
          { label: 'Published', value: 'published' },
          { label: 'Draft', value: 'draft' },
          { label: 'Needs Review', value: 'needs_review' },
        ],
      },
    ],
    actions: {
      create: {
        enabled: true,
      },
      view: {
        enabled: true,
      },
      edit: {
        enabled: true,
      },
      delete: {
        enabled: true,
      },
    },
  },

  detailView: {
    sections: [
      {
        title: 'Basic Information',
        description: 'Core neighborhood details',
        fields: ['featuredImageLarge', 'name', 'status', 'greaterArea', 'description'],
        defaultExpanded: true,
      },
      {
        title: 'Location Information',
        description: 'Geographic details',
        fields: ['country', 'state', 'city'],
        defaultExpanded: true,
      },
      {
        title: 'System Information',
        description: 'Timestamps and system data',
        fields: ['createdAt', 'updatedAt'],
        defaultExpanded: true,
      },
    ],
  },

  form: {
    sections: [
      {
        title: 'General',
        description: 'General Information about the Neighborhood',
        fields: ['featuredImageId', 'name', 'status', 'description'],
        columns: 1,
      },
      {
        title: 'Location',
        description: 'Where is the Neighborhood located?',
        fields: ['greaterAreaId', 'country', 'state', 'city'],
        columns: 2,
      },
    ],
    validation: {
      name: {
        required: true,
        min: 2,
        max: 200,
      },
      status: {
        required: true,
      },
      greaterAreaId: {
        required: true,
      },
      country: {
        required: true,
      },
      state: {
        required: true,
      },
      city: {
        required: true,
      },
    },
  },
};
